import React, { useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { inr, setSeo, FREE_SHIPPING_THRESHOLD, SHIPPING_FEE } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function CartPage() {
  const { items, subtotal, updateQty, removeItem, lineKey } = useCart();
  const navigate = useNavigate();
  const shipping = subtotal >= FREE_SHIPPING_THRESHOLD || subtotal === 0 ? 0 : SHIPPING_FEE;
  const metaFor = useMemo(
    () => Object.fromEntries(items.map((it) => [lineKey(it), [it.color, it.size].filter(Boolean).join(" · ")])),
    [items, lineKey],
  );

  useEffect(() => setSeo("Shopping Bag | Adam's The Islamic Boutique", "Review your shopping bag."), []);

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <ShoppingBag className="w-14 h-14 text-champagne/50 mx-auto mb-5" />
        <h1 className="font-serif text-4xl text-espresso mb-3">Your Bag is Empty</h1>
        <p className="text-espresso/50 mb-8">Looks like you haven't added anything yet.</p>
        <Link to="/shop" className="inline-block bg-espresso text-ivory px-10 py-3.5 rounded-full text-sm" data-testid="empty-cart-shop">Continue Shopping</Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-8 lg:py-12">
      <h1 className="font-serif text-4xl lg:text-5xl text-espresso mb-10">Shopping Bag</h1>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-10">
        <div className="space-y-6">
          {items.map((it) => {
            const k = lineKey(it);
            return (
              <div key={k} className="flex gap-4 lg:gap-6 pb-6 border-b border-champagne/20" data-testid={`cart-page-item-${it.slug}`}>
                <Link to={`/product/${it.slug}`}><img src={it.image} alt={it.name} className="w-24 h-32 lg:w-28 lg:h-36 object-cover rounded-xl" /></Link>
                <div className="flex-1 flex flex-col">
                  <div className="flex justify-between">
                    <Link to={`/product/${it.slug}`} className="font-serif text-xl text-espresso">{it.name}</Link>
                    <button onClick={() => removeItem(k)} data-testid={`cart-page-remove-${it.slug}`}><Trash2 className="w-5 h-5 text-espresso/40 hover:text-[#a13b2f]" /></button>
                  </div>
                  <p className="text-sm text-espresso/50 mt-1">{metaFor[k]}</p>
                  <div className="mt-auto flex items-center justify-between pt-4">
                    <div className="flex items-center border border-espresso/20 rounded-full">
                      <button onClick={() => updateQty(k, it.quantity - 1)} className="w-9 h-9 flex items-center justify-center"><Minus className="w-4 h-4" /></button>
                      <span className="w-8 text-center text-sm">{it.quantity}</span>
                      <button onClick={() => updateQty(k, it.quantity + 1)} className="w-9 h-9 flex items-center justify-center"><Plus className="w-4 h-4" /></button>
                    </div>
                    <span className="font-semibold text-espresso">{inr(it.price * it.quantity)}</span>
                  </div>
                </div>
              </div>
            );
          })}
          <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-espresso gold-underline">Continue Shopping</Link>
        </div>

        <div className="bg-beige/50 rounded-3xl p-7 h-fit lg:sticky lg:top-32">
          <h2 className="font-serif text-2xl text-espresso mb-6">Order Summary</h2>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-espresso/60">Subtotal</span><span className="font-medium" data-testid="cart-subtotal">{inr(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-espresso/60">Shipping</span><span className="font-medium">{shipping === 0 ? "Free" : inr(shipping)}</span></div>
            {shipping > 0 && <p className="text-xs text-champagne">Add {inr(FREE_SHIPPING_THRESHOLD - subtotal)} more for free shipping</p>}
          </div>
          <div className="border-t border-champagne/25 mt-5 pt-5 flex justify-between items-center">
            <span className="font-serif text-lg text-espresso">Total</span>
            <span className="font-serif text-2xl text-espresso" data-testid="cart-total">{inr(subtotal + shipping)}</span>
          </div>
          <button onClick={() => navigate("/checkout")} className="w-full bg-espresso text-ivory py-4 rounded-full text-sm font-medium mt-6 hover:bg-espresso/90 flex items-center justify-center gap-2" data-testid="cart-checkout-button">Proceed to Checkout <ArrowRight className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}
